import { useEffect } from "react";

const DEFAULT_TITLE = "Kyrosity AI | Micro-Dramas, Serialized Video & Growth | AI-Native Studio";
// Versioned filename on purpose. LinkedIn, Facebook and WhatsApp cache an
// og:image by URL and will happily serve a stale card for weeks; changing the
// path is the only reliable way to force them past it. Bump the version
// whenever the card art changes.
const DEFAULT_OG_IMAGE = "https://www.kyrosity.ai/og-image-v2.png";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

// Every routed page mounts its own SEOHead, and every tag is re-set on mount —
// so nothing stale survives client-side navigation. (Crawlers that skip JS
// still get index.html's static homepage tags; fixing that requires SSG.)
export const SEOHead = ({ title, description, canonical, jsonLd, noindex = false }) => {
  useEffect(() => {
    const fullTitle = title || DEFAULT_TITLE;
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    const robots = noindex ? "noindex, nofollow" : "index, follow";
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "googlebot", robots);

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", DEFAULT_OG_IMAGE);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:url", canonical);
    upsertMeta("name", "twitter:image", DEFAULT_OG_IMAGE);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    } else {
      // No canonical (e.g. the noindexed 404) — a stale canonical/og:url from
      // the previous page must not survive here.
      document.querySelector('link[rel="canonical"]')?.remove();
      document.querySelector('meta[property="og:url"]')?.remove();
      document.querySelector('meta[name="twitter:url"]')?.remove();
    }

    // Per-route structured data. Pages must define their jsonLd object at
    // module level so the reference stays stable across renders.
    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-head", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      if (script) script.remove();
    };
  }, [title, description, canonical, jsonLd, noindex]);

  return null;
};

export default SEOHead;
